/**
 * Dashboard Page Component
 * 
 * This component displays the main dashboard with statistics,
 * charts, and recent activity overview.
 */

import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useTranslations } from '../context/TranslationsContext';
import { dashboardAPI, ordersAPI } from '../api';
import StatsCard from '../components/dashboard/StatsCard';
import RecentOrders from '../components/dashboard/RecentOrders';
import DashboardOrderDetailsModal from '../components/dashboard/DashboardOrderDetailsModal';

const DashboardPage = () => {
  const { state, actions } = useApp();
  const { translations } = useTranslations();
  const t = translations[state.currentLanguage];
  
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Only fetch if we have a token and are authenticated
      if (!state.token || !state.isAuthenticated) return;
      
      actions.setLoading(true);
      try {
        const [stats, orders] = await Promise.all([
          dashboardAPI.getStats(state.token),
          ordersAPI.getOrders(state.token, { limit: 10 })
        ]);
        
        actions.setDashboardStats(stats);
        actions.setOrders(orders);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        actions.setLoading(false);
      }
    };

    fetchDashboardData();
  }, [state.token, state.isAuthenticated]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US').format(price);
  };

  // Show loading while authentication is being checked
  if (state.authLoading) {
    return <div className="loading">{t.checkingIdentity}</div>;
  }

  // Show loading while data is being fetched
  if (state.loading) {
    return <div className="loading">{t.loadingData}</div>;
  }

  return (
    <div className="space-y-8">
      <div className="dashboard-header">
        <h2 className="section-title">{t.dashboard}</h2>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
        <StatsCard
          icon="📦"
          title={t.totalOrders}
          value={state.dashboardStats.total_orders || 0}
        />
        <StatsCard
          icon="👥"
          title={t.totalCustomers}
          value={state.dashboardStats.total_customers || 0}
        />
        <StatsCard
          icon="⏳"
          title={t.pendingOrders}
          value={state.dashboardStats.pending_orders || 0}
        />
        <StatsCard
          icon="✅"
          title={t.activeOrders}
          value={state.dashboardStats.active_orders || 0}
        />
        <StatsCard
          icon="🔄"
          title={t.pendingRetrievalRequests}
          value={state.dashboardStats.pending_retrievals || 0}
        />
        <StatsCard
          icon="✅"
          title={t.completedRetrievalRequests}
          value={state.dashboardStats.completed_retrievals || 0}
        />
        <StatsCard
          icon="💰"
          title={t.totalRevenue}
          value={`${formatPrice(state.dashboardStats.total_revenue || 0)} ${t.sar}`}
        />
        <StatsCard
          icon="📅"
          title={t.monthlyOrders}
          value={state.dashboardStats.monthly_orders || 0}
        />
      </div>

      {/* Recent Orders */}
        <RecentOrders orders={state.orders} onViewDetails={setSelectedOrder} />

      {/* Dashboard Order Details Modal */}
      {selectedOrder && (
        <DashboardOrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
